<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <!-- Partial: Alert -->
    <TaxReturnAlert>
      <sc-alert type="info">
        Please review the tax return using the index on the left to navigate
        through pages or the arrow buttons below. You can also download the
        draft for review. Once you have finished reviewing it, please click the
        <b>Next</b> button.
        <sc-button-group slot="button-group">
          <sc-button
            type="ghost"
            @click="downloadTaxReturn()"
            :is-loading="downloadingTaxReturn"
          >
            Download Draft
          </sc-button>
          <sc-button
            type="ghost"
            @click="$emit('finished-reviewing')"
            icon-right="arrow-right"
            :is-disabled="saving || pdfLoading || !hasNextStep"
          >
            Next
          </sc-button>
        </sc-button-group>
      </sc-alert>
    </TaxReturnAlert>

    <!-- Partial: Content -->
    <sc-flex>
      <aside class="sidebar hidden-sm-and-down" v-if="bookmarks.length > 0">
        <v-card>
          <v-card-title class="headline">Index</v-card-title>
          <v-flex class="caption ma-2">
            The index is generated from the PDF's bookmarks and the auto
            classification tool. You can click on a page to navigate to it.
          </v-flex>
          <v-list dense expand>
            <template v-for="(bookmark, index) in bookmarks">
              <v-list-group
                v-if="bookmark.items && bookmark.items.length > 0"
                :key="bookmark.title + ' ' + index"
                :id="`bookmark-${bookmark.pageIndex}`"
              >
                <template v-slot:activator>
                  <v-list-tile
                    :style="
                      pageWithinBookmark(bookmark)
                        ? 'background-color: var(--blue-100)'
                        : ''
                    "
                    @click="goToPage(bookmark.pageIndex)"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title style="text-decoration: underline">
                        {{ bookmark.title }}
                      </v-list-tile-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </template>

                <v-list
                  dense
                  expand
                  class="pl-0 ml-3 text-align-left pt-0 pb-0"
                >
                  <template v-for="(subItem, subIndex) in bookmark.items">
                    <v-list-group
                      v-if="subItem.items && subItem.items.length > 0"
                      :key="subItem.title + ' ' + subIndex"
                    >
                      <template v-slot:activator>
                        <v-list-tile @click="goToPage(subItem.pageIndex)">
                          <v-list-tile-content>
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <span>{{ subItem.title }}</span>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </v-list-tile>
                      </template>
                      <v-list
                        dense
                        expand
                        class="pl-0 ml-3 text-align-left pt-0 pb-0"
                      >
                        <template
                          v-for="(thirdItem, thirdIndex) in subItem.items"
                        >
                          <v-list-group
                            v-if="thirdItem.items && thirdItem.items.length > 0"
                            :key="thirdItem.title + ' ' + thirdIndex"
                          >
                            <template v-slot:activator>
                              <v-list-tile
                                @click="goToPage(thirdItem.pageIndex)"
                              >
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <span>{{ thirdItem.title }}</span>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile-content>
                              </v-list-tile>
                            </template>
                            <v-list
                              dense
                              expand
                              class="pl-0 ml-3 text-align-left pt-0 pb-0"
                            >
                              <template
                                v-for="(fourthItem,
                                fourthIndex) in thirdItem.items"
                              >
                                <v-list-group
                                  v-if="
                                    fourthItem.items &&
                                      fourthItem.items.length > 0
                                  "
                                  :key="fourthItem.title + ' ' + fourthIndex"
                                >
                                  <template v-slot:activator>
                                    <v-list-tile
                                      @click="goToPage(fourthItem.pageIndex)"
                                    >
                                      <v-list-tile-content>
                                        <v-list-tile-title>
                                          <v-layout
                                            justify-space-between
                                            align-center
                                          >
                                            <span>{{ fourthItem.title }}</span>
                                          </v-layout>
                                        </v-list-tile-title>
                                      </v-list-tile-content>
                                    </v-list-tile>
                                  </template>
                                  <v-list-tile
                                    v-for="fifthItem in fourthItem.items"
                                    :key="
                                      fifthItem.title +
                                        ' ' +
                                        fifthItem.pageIndex
                                    "
                                    @click="goToPage(fifthItem.pageIndex)"
                                    :style="
                                      selectedThumbnail === fifthItem.pageIndex
                                        ? 'background-color: var(--blue-100)'
                                        : {}
                                    "
                                    class="pl-4"
                                  >
                                    <v-list-tile-title>
                                      <v-layout
                                        justify-space-between
                                        align-center
                                      >
                                        <v-layout justify-start align-center>
                                          <span style="max-width: 250px;">{{
                                            fifthItem.title
                                          }}</span>
                                          <v-chip
                                            v-if="
                                              isFilingInstructionsPage(
                                                fifthItem.pageIndex,
                                              )
                                            "
                                            small
                                            class="my-0 py-0"
                                          >
                                            Filing Instructions
                                          </v-chip>
                                        </v-layout>
                                      </v-layout>
                                    </v-list-tile-title>
                                  </v-list-tile>
                                </v-list-group>
                                <v-list-tile
                                  v-else-if="fourthItem.pageIndex"
                                  :key="
                                    fourthItem.title +
                                      ' ' +
                                      fourthItem.pageIndex
                                  "
                                  @click="goToPage(fourthItem.pageIndex)"
                                  :style="
                                    selectedThumbnail === fourthItem.pageIndex
                                      ? 'background-color: var(--blue-100)'
                                      : {}
                                  "
                                  class="pl-4"
                                >
                                  <v-list-tile-title>
                                    <v-layout
                                      justify-space-between
                                      align-center
                                    >
                                      <v-layout justify-start align-center>
                                        <span style="max-width: 250px;">{{
                                          fourthItem.title
                                        }}</span>
                                        <v-chip
                                          v-if="
                                            isFilingInstructionsPage(
                                              fourthItem.pageIndex,
                                            )
                                          "
                                          small
                                          class="my-0 py-0"
                                        >
                                          Filing Instructions
                                        </v-chip>
                                      </v-layout>
                                    </v-layout>
                                  </v-list-tile-title>
                                </v-list-tile>
                              </template>
                            </v-list>
                          </v-list-group>
                          <v-list-tile
                            v-else-if="thirdItem.pageIndex"
                            :key="thirdItem.title + ' ' + thirdItem.pageIndex"
                            @click="goToPage(thirdItem.pageIndex)"
                            :style="
                              selectedThumbnail === thirdItem.pageIndex
                                ? 'background-color: var(--blue-100)'
                                : {}
                            "
                            class="pl-4"
                          >
                            <v-list-tile-title>
                              <v-layout justify-space-between align-center>
                                <v-layout justify-start align-center>
                                  <span style="max-width: 250px;">{{
                                    thirdItem.title
                                  }}</span>
                                  <v-chip
                                    v-if="
                                      isFilingInstructionsPage(
                                        thirdItem.pageIndex,
                                      )
                                    "
                                    small
                                    class="my-0 py-0"
                                  >
                                    Filing Instructions
                                  </v-chip>
                                </v-layout>
                              </v-layout>
                            </v-list-tile-title>
                          </v-list-tile>
                        </template>
                      </v-list>
                    </v-list-group>
                    <v-list-tile
                      v-else-if="subItem.pageIndex"
                      :key="subItem.title + ' ' + subItem.pageIndex"
                      @click="goToPage(subItem.pageIndex)"
                      :style="
                        selectedThumbnail === subItem.pageIndex
                          ? 'background-color: var(--blue-100)'
                          : {}
                      "
                      class="pl-4"
                    >
                      <v-list-tile-title>
                        <v-layout justify-space-between align-center>
                          <v-layout justify-start align-center>
                            <span style="max-width: 250px;">{{
                              subItem.title
                            }}</span>
                            <v-chip
                              v-if="isFilingInstructionsPage(subItem.pageIndex)"
                              small
                              class="my-0 py-0"
                            >
                              Filing Instructions
                            </v-chip>
                          </v-layout>
                        </v-layout>
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>
                </v-list>
              </v-list-group>

              <v-list-tile
                v-else-if="bookmark.pageIndex"
                :key="bookmark.title + ' ' + index + 'no items'"
                :id="`bookmark-${bookmark.pageIndex}`"
                @click="goToPage(bookmark.pageIndex)"
                :style="
                  selectedThumbnail === bookmark.pageIndex
                    ? 'background-color: var(--blue-100)'
                    : ''
                "
              >
                <v-list-tile-title>
                  <v-layout align-center>
                    <span
                      style="max-width: 250px;"
                      class="mr-2"
                      :style="
                        bookmark.title.startsWith('Page') ? 'width: 50px;' : ''
                      "
                    >
                      {{ bookmark.title }}
                    </span>
                    <v-chip
                      v-if="isFilingInstructionsPage(bookmark.pageIndex)"
                      small
                      class="my-0 py-0"
                    >
                      Filing Instructions
                    </v-chip>
                  </v-layout>
                </v-list-tile-title>
              </v-list-tile>
            </template>
          </v-list>
        </v-card>
      </aside>
      <!-- State: Loading -->
      <v-layout wrap>
        <sc-flex
          v-if="pdfLoading"
          :is-centered="true"
          :is-column="true"
          :is-full-width="true"
        >
          <br />
          <v-progress-circular indeterminate color="primary" />
        </sc-flex>
        <!-- State: Partial Pages -->
        <section class="pdf-page-container" v-else>
          <div class="pdf-review-step__navigation">
            <sc-button
              type="secondary"
              @click="prevPage"
              :is-disabled="currentPage === 1"
              :is-icon-only="true"
              icon-left="arrow-left"
              size="small"
              >Previous</sc-button
            >
            <v-chip class="my-2 mb-1 primary white--text" small>
              Page {{ currentPage }} of {{ pageCount }}
            </v-chip>
            <sc-button
              type="secondary"
              @click="nextPage"
              :is-disabled="currentPage === pageCount"
              :is-icon-only="true"
              icon-right="arrow-right"
              size="small"
              >Next</sc-button
            >
          </div>
          <v-layout justify-center>
            <v-layout column align-center>
              <img
                v-if="currentPageImage"
                :src="currentPageImage.thumbnail"
                class="thumbnail"
              />
            </v-layout>
          </v-layout>
        </section>
      </v-layout>
    </sc-flex>
  </sc-flex>
</template>

<script>
import API from '@/api';
import { PDFDocument } from 'pdf-lib';
import pdf from 'vue-pdf';
import TaxReturnAlert from './components/TaxReturnAlert';
import { generatePdfThumbnails } from '@/utils/pdf';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';

export default {
  components: {
    pdf,
    TaxReturnAlert,
  },

  props: {
    companyId: {
      type: String,
      default: undefined,
    },

    taxReturn: {
      type: Object,
      default: null,
    },

    hasNextStep: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bookmarks: [],
      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfLoaded: [],
      pageImages: [],
      isScrolled: false,
      saving: undefined,
      downloadingTaxReturn: false,
      selectedThumbnail: null,
      currentPage: 1,
    };
  },
  mixins: [UtilsMixins, FirmPreferenceMixins],
  methods: {
    pageLoaded(page) {
      this.pdfLoaded[page] = true;
    },

    async downloadTaxReturn() {
      try {
        this.downloadingTaxReturn = true;
        const response = await API.taxReturn.downloadTaxReturn({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        });

        this.downloadDocumentByLink(response.data.link);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading tax return.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.downloadingTaxReturn = false;
      }
    },

    async extractBookmarks(pdfSrc) {
      try {
        const pdflib = window.pdfjsLib;
        pdflib.GlobalWorkerOptions.workerSrc =
          'https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.mjs';

        const loadingTask = pdflib.getDocument({ url: pdfSrc });
        const pdf = await loadingTask.promise; // Store pdf.js document instance

        this.extractBookmarksFromPdf(pdf);
      } catch (error) {
        console.error('Error extracting bookmarks:', error);
      }
    },

    async extractBookmarksFromPdf(pdf) {
      try {
        const outline = await pdf.getOutline();

        if (!outline) {
          this.bookmarks = [];
        } else {
          const processBookmarks = async bookmarks => {
            const processed = await Promise.all(
              bookmarks.map(async bookmark => {
                let pageIndex = null;

                // Extract page index correctly from dest[0]
                if (
                  bookmark.dest &&
                  bookmark.dest.length > 0 &&
                  bookmark.dest[0]
                ) {
                  try {
                    const resolvedPageIndex = await pdf.getPageIndex(
                      bookmark.dest[0],
                    );
                    pageIndex = resolvedPageIndex + 1; // Convert to 1-based index
                  } catch (error) {
                    console.warn(
                      `Failed to resolve page index for: ${bookmark.title}`,
                      error,
                    );
                  }
                }

                return {
                  title: bookmark.title,
                  pageIndex: pageIndex,
                  items: bookmark.items
                    ? await processBookmarks(bookmark.items)
                    : [],
                };
              }),
            );

            return processed;
          };

          let processedBookmarks = await processBookmarks(outline);

          // If there's only one top-level bookmark and it has items, use its items as root
          if (
            processedBookmarks.length === 1 &&
            processedBookmarks[0].items.length > 0
          ) {
            this.bookmarks = processedBookmarks[0].items;
          } else {
            this.bookmarks = processedBookmarks;
          }
        }

        this.insertSkippedPagesIntoBookmarks();
      } catch (error) {
        console.error('Error processing bookmarks:', error);
      }
    },

    insertSkippedPagesIntoBookmarks() {
      // Convert clientVisiblePages into a Set for quick lookup
      const visiblePageSet = new Set(this.clientVisiblePages.map(p => p.page));

      // Filter existing bookmarks to only keep those in clientVisiblePages
      this.bookmarks = this.bookmarks.filter(bookmark => {
        // First filter the items within each bookmark
        if (bookmark.items) {
          bookmark.items = bookmark.items.filter(
            item => item.pageIndex && visiblePageSet.has(item.pageIndex),
          );
        }
        // Then check if the bookmark itself should be kept
        return bookmark.pageIndex && visiblePageSet.has(bookmark.pageIndex);
      });

      // Get a list of all pages that are already in bookmarks
      const existingPageIndexes = new Set(this.bookmarksFlattened);

      // Add only pages that aren't already in bookmarks
      this.clientVisiblePages.forEach(visiblePage => {
        if (!existingPageIndexes.has(visiblePage.page)) {
          const clientPageNumber = this.clientVisiblePages.findIndex(
            p => p.page === visiblePage.page,
          );
          this.bookmarks.push({
            pageIndex: visiblePage.page,
            title: `Page ${clientPageNumber + 1}`,
            items: [],
          });
        }
      });

      // Sort bookmarks in ascending order of pageIndex to maintain order
      this.bookmarks.sort((a, b) => a.pageIndex - b.pageIndex);
    },

    goToPage(pageIndex) {
      const visiblePageIndex = this.clientVisiblePages.findIndex(
        page => page.page === pageIndex,
      );

      if (visiblePageIndex !== -1) {
        this.currentPage = visiblePageIndex + 1;
        this.selectedThumbnail = pageIndex;
      }
    },

    pageExists(pageIndex) {
      return this.clientVisiblePages.some(page => page.page === pageIndex);
    },

    isFilingInstructionsPage(pageIndex) {
      if (this.showFilingInstructionsToClients) {
        return this.taxReturn.filing_instructions.some(
          instruction => instruction.page === pageIndex,
        );
      }
      return false;
    },

    pageWithinBookmark(bookmark) {
      return bookmark.items.some(
        item => item.pageIndex === this.selectedThumbnail,
      );
    },

    nextPage() {
      if (this.currentPage < this.pageCount) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    updateCurrentPage(event) {
      this.currentPage = event.target.value;
    },
  },

  computed: {
    pageCount() {
      return this.clientVisiblePages.length;
    },
    isLoading() {
      return !(
        this.pdfLoaded &&
        this.pdfLoaded.every(isLoaded => isLoaded) &&
        this.taxReturn &&
        this.taxReturn.tax_return_pages &&
        this.taxReturn.tax_return_pages.length === this.pdfLoaded.length
      );
    },
    ...mapGetters({
      currentUser: 'user',
    }),
    clientVisiblePages() {
      if (this.showFilingInstructionsToClients) {
        return this.taxReturn.tax_return_pages
          .concat(this.taxReturn.filing_instructions)
          .sort((a, b) => a.page - b.page)
          .filter(
            (page, index, self) =>
              index === self.findIndex(t => t.page === page.page),
          );
      } else {
        return this.taxReturn.tax_return_pages;
      }
    },
    filteredPageImages() {
      return this.pageImages.filter(i =>
        this.clientVisiblePages.find(p => p.page === i.page),
      );
    },
    currentPageImage() {
      return this.filteredPageImages[this.currentPage - 1];
    },
    bookmarksFlattened() {
      const flattenBookmarks = bookmarks => {
        let pages = [];
        bookmarks.forEach(bookmark => {
          if (bookmark.pageIndex) {
            pages.push(bookmark.pageIndex);
          }
          if (bookmark.items && bookmark.items.length > 0) {
            pages = pages.concat(flattenBookmarks(bookmark.items));
          }
        });
        return pages;
      };
      return flattenBookmarks(this.bookmarks);
    },
  },

  async created() {
    try {
      this.pdfLoading = true;

      const response = await API.integration.downloadDocument({
        companyId: this.companyId,
        link: this.taxReturn.watermark_enabled
          ? this.taxReturn.watermarked_file
          : this.taxReturn.original_file,
      });

      const pdfSrc =
        response &&
        response.data &&
        response.data.document.data &&
        response.data.document.data.link;

      this.extractBookmarks(pdfSrc);
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      this.pdfDoc = await PDFDocument.load(pdfBytes);
      this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });
      this.pageImages = await generatePdfThumbnails(this.pdfData, 1000);
    } catch (error) {
      console.error(error);
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="css" scoped>
.toolbar {
  position: sticky;
  top: calc(72px + 56px);
  z-index: 1;

  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: center;
  padding: 0 8px;
  width: 100%;

  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.pdf-page-container {
  position: relative;
  z-index: 0;

  margin: 0 auto;
  width: 100%;
  flex: auto;
  overflow-y: auto;

  height: 100%;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
}
.pdf-page-container > div {
  position: relative;

  z-index: 1;
}

.pdf-page-container img {
  max-width: 100%;
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  min-width: 300px;
  max-width: 300px;
  overflow: scroll;
  padding: 0px 0px 64px;

  background-color: var(--gray-100);
  border-right: 1px solid var(--gray-200);
}

@media (max-width: 1260px) {
  .sidebar {
    min-height: calc(100vh - 157px);
    max-height: calc(100vh - 157px);
  }
}

.thumbnail {
  display: flex;
  width: 700px;
  max-width: 100%;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  position: relative;
}

.pdf-review-step__navigation {
  position: sticky !important;
  top: 0;
  z-index: 2 !important;

  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 4px 8px;
  width: 100%;

  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-200);
}
</style>
